import { useState } from 'react';

import { ChevronDown } from 'lucide-react';

import { ChatConversation } from '@/hooks/useChat';
import { useConversationSearch } from '@/hooks/useConversationSearch';
import { useGroupedConversations } from '@/hooks/useGroupedConversations';

import { Button } from '@/components/ui/Button';
import { Command, CommandEmpty, CommandList } from '@/components/ui/Command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { ConversationGroup } from './ConversationGroup';
import { ConversationSearch } from './ConversationSearch';

interface ConversationHistoryDropdownProps {
  conversations: ChatConversation[];
  currentConversationId: string | null;
  onConversationSelect: (id: string) => void;
}

// eslint-disable-next-line max-lines-per-function
export const ConversationHistoryDropdown = (
  props: ConversationHistoryDropdownProps
) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const currentConversation = props.conversations.find(
    (conversation) => conversation.id === props.currentConversationId
  );

  const groupedConversations = useGroupedConversations(props.conversations);
  const filteredGroups = useConversationSearch(
    groupedConversations,
    searchQuery
  );

  const handleSelect = (conversationId: string) => {
    props.onConversationSelect(conversationId);
    setOpen(false);
    setSearchQuery('');
  };

  const hasConversations =
    filteredGroups.today.length > 0 ||
    filteredGroups.previous7Days.length > 0 ||
    filteredGroups.previous30Days.length > 0 ||
    filteredGroups.older.length > 0;

  const conversationGroups = [
    { key: 'today', heading: 'Today', data: filteredGroups.today },
    {
      key: 'previous7Days',
      heading: 'Previous 7 days',
      data: filteredGroups.previous7Days,
    },
    {
      key: 'previous30Days',
      heading: 'Previous 30 days',
      data: filteredGroups.previous30Days,
    },
    { key: 'older', heading: 'Older', data: filteredGroups.older },
  ];

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          aria-expanded={open}
          className="w-full min-w-0 justify-between rounded-md bg-background hover:bg-background"
        >
          <span className="truncate">
            {currentConversation?.title || 'Previous conversations'}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="!min-w-0 w-[--radix-dropdown-menu-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandList className="max-h-[300px]">
            {!hasConversations && !searchQuery && (
              <CommandEmpty>No conversations yet.</CommandEmpty>
            )}
            {!hasConversations && searchQuery && (
              <CommandEmpty>No conversations found.</CommandEmpty>
            )}

            {conversationGroups.map((group) => (
              <ConversationGroup
                key={group.key}
                heading={group.heading}
                conversations={group.data}
                currentConversationId={props.currentConversationId}
                onSelect={handleSelect}
              />
            ))}
          </CommandList>

          <ConversationSearch value={searchQuery} onChange={setSearchQuery} />
        </Command>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
